/**
 * SKE-4539. The still image a tag is positioned against.
 *
 * Tag coordinates are meaningless without a fixed frame of reference, and the
 * tag editor can only render an image. For a photo that is the photo. For a
 * video, Meta accepts user tags on feed video and on both story types, so the
 * customer needs something to click: the cover frame they chose, and failing
 * that the server-generated first frame every uploaded video carries.
 *
 * Returns null when a video has neither yet, which happens while the upload is
 * still being processed. That is a "try again in a moment", not a "videos
 * cannot be tagged".
 */

export type TagStillMediaFile = {
  url?: string | null;
  originalUrl?: string | null;
  thumbnailUrl?: string | null;
  isVideo?: boolean;
  meta?: { mime?: string } | null;
};

type TagStillPost = {
  mediaFiles?: TagStillMediaFile[] | null;
  videoCoverPreview?: string | null;
  url?: string | null;
  originalUrl?: string | null;
  thumbnailUrl?: string | null;
  isVideo?: boolean;
  meta?: { mime?: string } | null;
};

const nonEmpty = (value?: string | null): string | null => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
};

const isHeicMime = (mime?: string | null) =>
  typeof mime === "string" && /heic|heif/i.test(mime);

const looksLikeVideo = (url?: string | null) =>
  typeof url === "string" && /\.(mp4|mov|m4v|webm)(\?|$)/i.test(url);

/** The first asset, whether the post uses `mediaFiles` or the older flat shape. */
export const firstTagAsset = (post: TagStillPost): TagStillMediaFile =>
  post?.mediaFiles && post.mediaFiles.length > 0 ? post.mediaFiles[0] : post;

export const isVideoAsset = (asset: TagStillMediaFile): boolean =>
  Boolean(asset?.isVideo) || looksLikeVideo(asset?.url);

/** A candidate still, but only if it is actually an image. */
const imageOnly = (url?: string | null): string | null => {
  const value = nonEmpty(url);
  return value && !looksLikeVideo(value) ? value : null;
};

/**
 * The still for ONE asset, given the post-level candidates that may stand in
 * for a video frame.
 *
 * SKE-4617. Split out of `resolveTagStillUrl` because a carousel slide past the
 * first has no post-level cover to fall back on: `videoCoverPreview` and the
 * post's `thumbnailUrl` both describe slide one.
 */
export function resolveMediaStillUrl(
  asset: TagStillMediaFile | null | undefined,
  videoCandidates: Array<string | null | undefined> = []
): string | null {
  if (!asset) return null;

  if (!isVideoAsset(asset)) {
    // A HEIC original cannot be rendered by the browser; the thumbnail can.
    if (isHeicMime(asset?.meta?.mime)) return nonEmpty(asset.thumbnailUrl);
    return nonEmpty(asset.url) ?? nonEmpty(asset.originalUrl);
  }

  /*
   * SKE-4612. Each candidate must be an IMAGE, not merely present.
   *
   * A video's `thumbnailUrl` is sometimes the video itself, which is why
   * `utils/media.ts` guards the same field with `isImagePreviewUrl` for
   * SKE-3646. This resolver did not, so it could hand an `.mp4` to the tag
   * editor as its `imageUrl`, and the editor renders that in an `<img>`: the
   * canvas is blank and the customer cannot place a tag, with nothing saying
   * why. There is no second candidate behind it now that the dead `coverFrames`
   * branch is gone, so the guard has to be here.
   */
  for (const candidate of videoCandidates) {
    const image = imageOnly(candidate);
    if (image) return image;
  }
  return null;
}

export function resolveTagStillUrl(post: TagStillPost): string | null {
  const asset = firstTagAsset(post ?? {});
  return resolveMediaStillUrl(asset, [
    post?.videoCoverPreview,
    asset?.thumbnailUrl,
    post?.thumbnailUrl,
  ]);
}
